import type { Capabilities, OrgPrivilege } from "@ondo/shared";

/**
 * What a portal IS, in one object.
 *
 * The shell used to import `DESTINATIONS`, `DESTINATION_HEADERS` and
 * `NOT_YET_COPY` from `src/collector/` directly, which made it the collector's
 * shell wearing a role-generic stylesheet: every component was typed on
 * `CollectorDestination`, so a second portal could not be handed to it at all.
 * A spec inverts that — the shell is HANDED what a portal contains, and knows
 * about no role in particular.
 *
 * `CollectorWorkspace.tsx` promised this in a comment for as long as the file
 * has existed ("this shell is role-generic — the estate workspace will reuse
 * every class here with a different token"), and `index.css` promised it again
 * where `--shell-nav` is defined. This is the type that makes it true.
 */

/**
 * Which of the sidebar's two stacks a destination sits in.
 *
 * `main` is the work — the holdings and what you do to them. `foot` is the
 * account: what has been sent to you, what ONDO is, what has happened. Pinned
 * to the bottom above the identity card and separated by whitespace, not a
 * rule, because they are two lists of the same kind of thing and a line between
 * them would claim otherwise.
 */
export type NavGroup = "main" | "foot";

/**
 * Whether the registry can answer the question a destination asks.
 *
 * Not a feature flag on a finished feature. A `planned` entry is still
 * reachable, because a greyed-out nav item tells the reader nothing while a
 * page naming what is missing tells them something. `unlisted` is a destination
 * with no nav item — reachable and addressable, but arrived at from a card
 * rather than from the rail, which is what a work record is.
 */
export type DestinationState = "ready" | "planned" | "unlisted";

/**
 * Whether a destination takes a fourth URL segment, and whether it needs one.
 *
 * A work REQUIRES its record — a work record with no work is not a screen. A
 * viewing room ALLOWS one: the list is a screen in its own right, and a room id
 * opens that room on top of it.
 */
export type RecordRule = "required" | "optional" | "none";

export interface PortalDestination<D extends string = string> {
  id: D;
  label: string;
  glyph: string;
  state: DestinationState;
  group: NavGroup;
  /**
   * A privilege this destination's screen needs, or absent for the ordinary
   * case — OND-61.
   *
   * NOT a feature flag and not a `planned` state, which is why it is a separate
   * field from both. `planned` means the registry cannot answer the question
   * yet and the page says so; this means the registry can answer it and this
   * SEAT may not ask. The difference is what the reader should be shown: a
   * planned destination stays in the rail because "a greyed-out nav item tells
   * the reader nothing while a page naming what is missing tells them
   * something", and a destination somebody else's seat may open is simply not
   * theirs — advertising it would publish a colleague's privilege to them.
   *
   * It governs the RAIL only. The destination stays addressable, because a
   * bookmark or a shared URL will reach it, and the screen behind it refuses on
   * its own — from the database, which is the only thing that decides. Hiding a
   * nav item grants nothing and withholds nothing.
   */
  requires?: OrgPrivilege;
}

/**
 * Whose workspace this is: the firm, or the reader on a personal one.
 *
 * A pair rather than a string because a name alone cannot say WHICH firm — two
 * advisories can be called the same thing, and the ONDO id is the identifier the
 * rest of the product addresses one by.
 */
export interface PortalSubject {
  name: string;
  ondoId: string;
}

/** The heading and the line under it, for one destination. */
export interface DestinationHeader {
  title: string;
  /**
   * The line under the title, or a function of the workspace's SUBJECT.
   *
   * The collector's are all fixed sentences about the screen. The agent's name
   * the firm on every one — "Marlow & Finch Art Advisory — confirm or dispute
   * information others have asked you to verify" — because a member of an
   * advisory is reading a screen about the FIRM's mandates and not their own,
   * and a subtitle that said "you" would be quietly wrong about whose they are.
   *
   * The subject is the organisation's display name, or the reader's own on a
   * personal workspace, which is what makes the same sentence true for an
   * independent advisor with no firm at all.
   *
   * It carries the ONDO id as well as the name, because two firms can share a
   * name and the id is what says which one this workspace is. Which of the two a
   * subtitle uses is the subtitle's own decision — the representations list
   * prints both, the verification inbox only the name, because there the id
   * would sit in the middle of a sentence.
   */
  subtitle: string | ((subject: PortalSubject) => string);
}

/** What a `planned` destination will do, and what has to exist first. */
export interface NotYetCopy {
  lead: string;
  detail: string;
}

/**
 * Where the three counts the shell fetches hang, or null for a portal with no
 * such item in its rail.
 *
 * Named rather than assumed to be the literals: a portal that lists none of
 * them should cost none of the requests, and the controller reads these to
 * decide.
 *
 * `settlements` is the third slot, and it was promised in
 * gallery-resale-settlements.html the day the screen shipped — "widen BadgeIds,
 * it has exactly two slots today" — and then not built. It counts what is
 * WAITING ON THE READER, the way `verifications` does and `notifications` does
 * not: a settlement the gallery has yet to collect or remit, or a remittance
 * the artist has yet to confirm or dispute. A gallery is told a settlement fell
 * due by notification; this is what keeps it visible after the notification
 * has been read.
 */
export interface BadgeIds<D extends string = string> {
  verifications: D | null;
  notifications: D | null;
  /**
   * Optional where the other two are required, and only for a reason of
   * timing: the slot was added while the estate portal was being written in a
   * parallel session, and a required field would have broken a file that was
   * not this commit's to edit. Absent reads as null. The estate is the portal
   * that most wants it — an artist's remittances land there — so it should be
   * declared the moment that spec is settled.
   */
  settlements?: D | null;
}

/**
 * One portal, erased to strings.
 *
 * The shell consumes this shape; the concrete portals are declared through
 * `definePortal`, which keeps the exhaustiveness the erased type cannot.
 */
export interface PortalSpec {
  /** The word under the mark in the rail — "Collector", "Agent Portal". */
  eyebrow: string;
  /** The nav's accessible name. */
  navLabel: string;
  destinations: readonly PortalDestination[];
  headers: Readonly<Record<string, DestinationHeader>>;
  notYet: Readonly<Record<string, NotYetCopy | undefined>>;
  /** Where `/:role/:org` with no destination redirects to. */
  defaultDestination: string;
  records: Readonly<Record<string, RecordRule>>;
  /** Where a `required` record with no record goes. The collector's is its collection. */
  recordFallback: string;
  badges: BadgeIds;
  /**
   * The destination whose heading follows its SUBJECT rather than the portal.
   *
   * A profile screen opens onto the reader in a personal workspace and onto the
   * entity in an organisation's, so "My Profile" is the wrong title over a
   * firm's record and its staff. Named here rather than keyed on the literal
   * `"profile"` inside the shell, so a portal that has no such screen says so.
   */
  profileAt: string | null;
  /**
   * The destination whose subtitle greets the reader by first name, if any.
   *
   * One per portal at most. A subtitle explaining what a dashboard is would be
   * explaining the obvious, which is the only reason a greeting belongs there.
   */
  greetsAt: string | null;
  /**
   * The header's one action, or null.
   *
   * Takes the capabilities rather than the role, because a workspace is a route
   * and not a permission: opening a collector workspace grants nothing, so it
   * must not be what switches a button on. Returns a destination id rather than
   * an href — only the shell knows which workspace it is rendering.
   */
  primaryAction: (
    capabilities: Capabilities,
    at: string,
  ) => { label: string; destination: string } | null;
  /**
   * The header's second action, or null — drawn before the primary, quieter.
   *
   * It exists because a screen's own actions were being drawn twice: the
   * Collections screen carried "New collection" and "Register Artwork" in a
   * row of its own beneath a header that already had "Register Artwork". One
   * place for a screen's actions is the header, and a frame can only hold a
   * screen's action as a LINK — so an action that opens something on the
   * screen names a `search` the screen reads (`FOCUS_PARAMS.create`), and the
   * screen holds the state. Gated on capabilities, like the primary, and for
   * the same reason.
   */
  secondaryAction: (
    capabilities: Capabilities,
    at: string,
  ) => { label: string; destination: string; search?: string } | null;
}

/**
 * Declare a portal, with its destination union checked.
 *
 * The three `Record<D, …>` fields are TOTAL at the declaration site, so adding
 * a destination without deciding its header, its record rule or its heading is
 * a COMPILE ERROR rather than a test — which is the property `recordRule`'s
 * old `if` chain could not have. The return type erases `D` because the shell
 * renders whichever portal it is handed and cannot be generic over all of them
 * at once; the cast is confined to this one function for that reason.
 */
export function definePortal<D extends string>(spec: {
  eyebrow: string;
  navLabel: string;
  destinations: readonly PortalDestination<D>[];
  headers: Record<D, DestinationHeader>;
  notYet: Partial<Record<D, NotYetCopy>>;
  defaultDestination: D;
  records: Record<D, RecordRule>;
  recordFallback: D;
  badges: BadgeIds<D>;
  profileAt: D | null;
  greetsAt: D | null;
  /**
   * `at` is a plain string on both sides, and cannot be `D`.
   *
   * A function taking `D` is not assignable to one taking `string` — parameters
   * are contravariant — so narrowing it here would make the erasure below a
   * lie rather than a cast. The RETURN keeps `D`, which is the half that
   * matters: a portal cannot name a destination it does not have.
   */
  primaryAction: (
    capabilities: Capabilities,
    at: string,
  ) => { label: string; destination: D } | null;
  /**
   * Required, though four of the five portals answer null: a default filled
   * in here would make the erasure a copy, and `portal.test.ts` pins that it
   * is a cast — the spec handed in is the spec the shell renders.
   */
  secondaryAction: (
    capabilities: Capabilities,
    at: string,
  ) => { label: string; destination: D; search?: string } | null;
}): PortalSpec {
  return spec as PortalSpec;
}
